/**
 * uiTokens — the central UI "Pflegetabelle" (BL-37, ART §9).
 *
 * The arena has ONE colour source (`core/palette.ts`) and ONE material
 * (`EmissiveMaterial`/`glowCache`). Layer 4 (the HUD) had neither — it derived
 * from two `PANEL` tokens, ran on `Courier New`, and scattered magic numbers.
 * This module is the HUD's single source of truth for everything *structural*:
 *
 *   - the §9 type-scale ROLES (VT323 + monospace fallback; `numeric` = tabular),
 *   - a named SPACING ladder (2/4/6/8/10/12/16/24),
 *   - the panel-MATERIAL tokens the engine-material chrome layer draws with
 *     (edge-glow colour/radius/opacity, inner-gradient stops, active/disabled
 *     deltas) — colour resolved from `COLORS`/`PANEL`, never a new gameplay hex,
 *   - the MOTION tokens (durations + `cubic-bezier`s that MIRROR the canvas
 *     easing vocab in `rendering/AnimationUtils.ts`, so DOM transitions and the
 *     canvas tweens share one feel).
 *
 * Nothing here is gameplay/identity colour: that stays central in
 * `core/palette.ts` / `COLORS` / `designTokens.PANEL`. New *UI* colour tokens
 * (text roles, glow opacities) live here and only here.
 *
 * Consumed by: `UIMaterial`/`LitPanel` (the chrome primitive), `UIChromeCanvas`
 * (the layer), and the relit HUD modules. BL-54 (skill cards), BL-55 (tower
 * previews) and BL-48 (enemy-reveal) build on these same tokens.
 */
import { COLORS } from "../core/constants.ts";
import { RARITY } from "../core/palette.ts";
import { PANEL } from "../rendering/designTokens.ts";

// ─── A. Type scale — the §9 roles (VT323 monospace; the terminal IS the brand) ─

/** The font stack every UI role uses. VT323 first (DOM-loaded early by
 *  `ensureUIFont`); `Courier New`/`monospace` is the no-flash fallback. */
export const UI_FONT_STACK = `'VT323', 'Courier New', monospace`;

export type TypeRoleName =
  | "hero"
  | "display"
  | "title"
  | "heading"
  | "subheading"
  | "body"
  | "label"
  | "caption"
  | "micro"
  | "numeric";

export interface TypeRole {
  /** Font size in px (VT323 reads small, so the scale runs a touch large). */
  size: number;
  /** `line-height` multiple. */
  lineHeight: number;
  /** `letter-spacing` in px. */
  letterSpacing: number;
  /** `font-variant-numeric` — `numeric` pins `tabular-nums` so digits align. */
  variantNumeric: "normal" | "tabular-nums";
  /** Default text colour for the role (from the central palette). */
  color: string;
}

/**
 * The §9 type roles (BL-77 — extended from the original five to a full ladder so
 * every band the app actually uses has a NAME, not a magic number). `hero` and
 * `display` are the glitch-accent slots (boot/title/overlay headlines, never
 * running body text); `micro` is the **DOM floor** (12px @ `--ui-scale: 1`) — no
 * UI text sits below it; `numeric` is the only role with `tabular-nums` so a
 * changing counter doesn't reflow. All colours come from `COLORS`.
 *
 * Sizes are the base px @ `--ui-scale: 1`; `typeRoleCss`/`applyTypeRole` emit
 * them as `calc(<px>px * var(--ui-scale, 1))` so the whole app scales from the
 * single `utils/uiScale` knob. VT323 reads small, so the ladder runs a touch
 * large with generous line-heights.
 */
export const UI_TYPE: Record<TypeRoleName, TypeRole> = {
  hero: {
    size: 150,
    lineHeight: 1.0,
    letterSpacing: 2,
    variantNumeric: "normal",
    color: COLORS.TEXT_GLITCH,
  },
  display: {
    size: 34,
    lineHeight: 1.05,
    letterSpacing: 2,
    variantNumeric: "normal",
    color: COLORS.TEXT_GLITCH,
  },
  title: {
    size: 24,
    lineHeight: 1.1,
    letterSpacing: 1.5,
    variantNumeric: "normal",
    color: COLORS.SENTINEL_GREEN,
  },
  heading: {
    size: 20,
    lineHeight: 1.15,
    letterSpacing: 1.5,
    variantNumeric: "normal",
    color: COLORS.SENTINEL_GREEN,
  },
  subheading: {
    size: 17,
    lineHeight: 1.2,
    letterSpacing: 1,
    variantNumeric: "normal",
    color: COLORS.TEXT_PRIMARY,
  },
  body: {
    size: 16,
    lineHeight: 1.5,
    letterSpacing: 0.5,
    variantNumeric: "normal",
    color: COLORS.TEXT_PRIMARY,
  },
  label: {
    size: 14,
    lineHeight: 1.35,
    letterSpacing: 0.75,
    variantNumeric: "normal",
    color: COLORS.TEXT_PRIMARY,
  },
  caption: {
    size: 13,
    lineHeight: 1.4,
    letterSpacing: 1,
    variantNumeric: "normal",
    color: COLORS.TEXT_SECONDARY,
  },
  micro: {
    size: 12,
    lineHeight: 1.3,
    letterSpacing: 1,
    variantNumeric: "normal",
    color: COLORS.TEXT_SECONDARY,
  },
  numeric: {
    size: 17,
    lineHeight: 1.2,
    letterSpacing: 1,
    variantNumeric: "tabular-nums",
    color: COLORS.TEXT_PRIMARY,
  },
};

/**
 * Wrap a px value as a `--ui-scale`-aware CSS length (BL-77). Every font-size in
 * the app — role-driven or a one-off offset like `caption.size - 2` — goes
 * through this (or `roleFontSize`) so the single `utils/uiScale` knob moves all
 * type together. `--ui-scale` is inherited from `#game-container`; it defaults
 * to `1` (index.html sets it statically), so this is byte-identical to a bare
 * `<px>px` until the knob is turned.
 *
 * It ALSO composes a second, DOM-only `--chrome-scale` multiplier (defaults to
 * `1`; raised only on the phone tier by `utils/chromeScale`). This is the
 * decouple seam: canvas text reads `getUiScale()` directly and never sees
 * `--chrome-scale`, so the phone's DOM chrome can be enlarged for legibility
 * WITHOUT inflating on-board canvas text (floating damage, field labels). At the
 * default `1` both vars are inert → byte-identical to the bare `<px>px`.
 */
export function withUiScale(px: number): string {
  return `calc(${px}px * var(--ui-scale, 1) * var(--chrome-scale, 1))`;
}

/** The `--ui-scale`-aware `font-size` value for a role (its base size scaled). */
export function roleFontSize(name: TypeRoleName): string {
  return withUiScale(UI_TYPE[name].size);
}

/** Build the CSS declarations for a role as a single string (for `cssText` /
 *  template injection). Keeps every role's typography in one place. */
export function typeRoleCss(name: TypeRoleName): string {
  const r = UI_TYPE[name];
  return [
    `font-family: ${UI_FONT_STACK}`,
    `font-size: ${withUiScale(r.size)}`,
    `line-height: ${r.lineHeight}`,
    `letter-spacing: ${r.letterSpacing}px`,
    `font-variant-numeric: ${r.variantNumeric}`,
    `color: ${r.color}`,
  ].join("; ");
}

/** Apply a role to a live element (mutates inline style). */
export function applyTypeRole(el: HTMLElement, name: TypeRoleName): void {
  const r = UI_TYPE[name];
  el.style.fontFamily = UI_FONT_STACK;
  el.style.fontSize = withUiScale(r.size);
  el.style.lineHeight = `${r.lineHeight}`;
  el.style.letterSpacing = `${r.letterSpacing}px`;
  el.style.fontVariantNumeric = r.variantNumeric;
  el.style.color = r.color;
}

// ─── B. Spacing ladder — named, so nothing hardcodes a gap ────────────────────

/** The 2/4/6/8/10/12/16/24 spacing ladder (px), named by role. */
export const UI_SPACE = {
  hair: 2,
  xxs: 4,
  xs: 6,
  sm: 8,
  md: 10,
  lg: 12,
  xl: 16,
  xxl: 24,
} as const;

export type SpaceName = keyof typeof UI_SPACE;

// ─── C. Panel material — the lit-CRT-surface tokens the chrome canvas draws ────

export type PanelState = "default" | "active" | "disabled";

export interface PanelMaterial {
  /** Emissive edge colour (the §6.1 hot edge). Defaults to the green identity. */
  edgeColor: string;
  /** Outer glow radius in px (the falloff width of the lit edge). */
  edgeRadius: number;
  /** Edge-glow opacity at the `default` state (0..1). */
  edgeOpacity: number;
  /** Corner radius of the panel rect in px. */
  cornerRadius: number;
  /** Inner-gradient stops (faint lit fill, top→bottom): [innerHex, outerHex]. */
  innerGradient: [string, string];
  /** Per-state multipliers on the edge intensity (active brighter, disabled dim). */
  stateIntensity: Record<PanelState, number>;
}

/**
 * The default panel material. Colour comes from `COLORS.SENTINEL_GREEN` /
 * `PANEL` — never a new gameplay hex. The inner gradient is a near-black lit
 * fill so the panel reads as a *surface* lit from its edge, not a flat box.
 */
export const UI_PANEL_MATERIAL: PanelMaterial = {
  edgeColor: COLORS.SENTINEL_GREEN,
  edgeRadius: 14,
  edgeOpacity: 0.5,
  cornerRadius: 6,
  innerGradient: [PANEL.BG_ELEVATED, PANEL.BG],
  stateIntensity: {
    default: 1,
    active: 1.6,
    disabled: 0.35,
  },
};

// ─── C2. UI bloom — the shared-WebGL bloom CALIBRATED for the HUD (BL-37b) ─────

/**
 * The UI bloom params (BL-37b / S87). The HUD chrome runs through a 2nd
 * instance of the arena's bloom pipeline (`WebGL2Effects`) — the SAME GLSL, so
 * the HUD and the battlefield are one material world (ART §9). But the chrome
 * is small, sharp, static and fine-lined where the arena is big, soft and
 * moving, so the arena's own uniform values would read blown-out or hazy. These
 * are the in-engine knobs, tuned FOR the UI (the only things that differ from
 * the arena — never the shaders):
 *
 *   - `threshold` ABOVE the arena's 0.5 → only the genuinely hot edges, glints
 *     and emissive cores catch fire; the near-black lit-surface fill stays dark
 *     (no whole-panel haze), which is also why text — drawn on the transparent
 *     DOM layer above, never a bloom source — can never bloom.
 *   - `intensityHigh/Medium` slightly UNDER the arena → lit hardware, premium
 *     and restrained, not a neon soup over a small panel.
 *   - `bloomDownscale: 1` (full-res blur, vs the arena's half-res) → a tight,
 *     defined *filament* edge instead of a foggy halo on fine chrome. Affordable
 *     because the UI bloom is dirty-redraw (it runs only when the chrome
 *     changes), not a per-frame cost.
 *   - `chromaOffset: 0` → chroma OFF for the UI; readability is sacred and a
 *     colour fringe near a glyph edge is an instant fail.
 *
 * Not colour — no new gameplay hex (the chrome's colour still comes from
 * `UI_PANEL_MATERIAL.edgeColor` → the central palette). These are the bloom
 * tuning only, kept here so the HUD's "look" has one home.
 */
export const UI_BLOOM = {
  threshold: 0.6,
  intensityHigh: 0.85,
  intensityMedium: 0.45,
  chromaOffset: 0,
  bloomDownscale: 1,
} as const;

// ─── D. Motion — durations + cubic-beziers MIRRORING AnimationUtils ────────────

/**
 * CSS `cubic-bezier`s that mirror the canvas easing vocab in
 * `rendering/AnimationUtils.ts`, so a DOM transition and a canvas tween of the
 * same name read identically:
 *   - `snap`   = ease-out-back  (overshoots >1 — the "punch")  ← AnimationUtils.snap
 *   - `settle` = ease-out-cubic (eases to rest, no overshoot)  ← AnimationUtils.settle
 *   - `glide`  = ease-in-out-quad (smooth continuous lerp)     ← AnimationUtils.glide
 *   - `decay`  = ease-out-expo  (fast out, long flat tail)     ← AnimationUtils.decay
 */
export const UI_EASE = {
  snap: "cubic-bezier(0.34, 1.56, 0.64, 1)",
  settle: "cubic-bezier(0.215, 0.61, 0.355, 1)",
  glide: "cubic-bezier(0.45, 0, 0.55, 1)",
  decay: "cubic-bezier(0.16, 1, 0.3, 1)",
} as const;

export type EaseName = keyof typeof UI_EASE;

/** Motion durations in ms. One place to retune the HUD's tempo. */
export const UI_DURATION = {
  /** Value-flash glint (currency tick, life lost, wave clear). */
  flash: 380,
  /** Panel / overlay settle-in (slide + tiny overshoot). */
  settle: 260,
  /** Micro-interaction hover/select lift. */
  hover: 140,
} as const;

export type DurationName = keyof typeof UI_DURATION;

// ─── E. Foil — the BL-59 trading-card premium layer (DEMOTES the ⌐ bracket) ────

/**
 * The unified card-system "foil" tokens (BL-59 enemies · BL-60 skills).
 *
 * The OLD premium signal was a corner **⌐-bracket** motif (the L-tick console
 * accents on `OperatorMascot`/`PreRunScreen`). The owner's "edel" CR demotes
 * that motif: the unified `TradingCard` frame lets the engine **material** carry
 * premium instead — a rarity-tiered **gold-foil** frame + a refined **circuit-
 * etch** accent (`UIMaterial.drawCircuitEtch`), not heavy brackets. The legendary
 * gold is the central `RARITY.legendary.mid` token (Glacier gold; BL-260
 * corrected a stale literal note here) — these are STRUCTURE / ANIMATION knobs
 * only; no new gameplay hex lives here (colour stays central in
 * `core/palette.ts`). The brackets that remain elsewhere (the comms console) are
 * untouched — only the card language moves off them.
 *
 * Consumed by `src/ui/TradingCard.ts` (BL-59) and `src/ui/EnemyHolo.ts`; BL-60's
 * skill Trainer cards reuse the same frame + these same tokens.
 */
export const UI_FOIL = {
  /** Scanline spacing (px) — the "being analysed" dossier texture. */
  scanlineGap: 3,
  /** The vertical scan-sweep period (ms) — the malware-analysis pass. */
  sweepMs: 4200,
  /** Specular catch-light sweep period (ms) for the legendary gold foil. */
  foilSweepMs: 2600,
  /** Sparkle / data-mote cap per card (additive, bloomed). Scaled down by tier. */
  moteCap: 16,
  /** Radial scanline-ray count behind the hero (the living foil field). */
  rayCount: 28,
  /** Circuit-etch corner reach (px) — the refined L-tick that replaces the ⌐. */
  etchReach: 14,
} as const;

// ─── F. Board chrome — the BL-62 shared "one picture" frame (3-column board) ───

/**
 * The board-FRAME chrome from the signed `docs/gameboard_mockup.html` (the
 * 3-column composition target). These are UI-CHROME *structure* values — the
 * column borders, the panel grounds, the corner-etch accent, and the shared
 * scanline opacity — NOT gameplay-entity colour (that stays central in
 * `core/palette.ts`). The single source the BL-62 composition reads from so the
 * whole board reads as ONE harmonious frame:
 *
 *   - `BoardChrome` (the ONE shared scanline + corner-etch layer) reads these;
 *   - the shell (`index.html`) MIRRORS these literals for first paint (it can't
 *     import TS), with a comment pointing here as the source of truth.
 *
 * The mockup hues weren't tokens yet, so they're added here centrally (per the
 * BL-62 brief) rather than scattered as inline hex.
 */
export const UI_CHROME = {
  /** Column-divider border (mockup `.left`/`.right` `border:1px solid #102a16`). */
  panelBorder: "#102a16",
  /** App-frame / tower-row border (mockup `.app`/`.twr` `#14301a`). */
  frameBorder: "#14301a",
  /** Panel ground gradient stop — top (mockup column `linear-gradient(180deg,#070b08,…`). */
  groundTop: "#070b08",
  /** Panel ground gradient stop — bottom (mockup `…,#05060b)`). */
  groundBottom: "#05060b",
  /** Corner-etch accent — the premium gold L-tick (central legendary gold). */
  etchAccent: RARITY.legendary.mid,
  /** Shared board-scanline overlay opacity (a subtle, board-wide lit-CRT texture). */
  scanOpacity: 0.4,
  /** Green highlight (mockup `--green-hi`). */
  greenHi: "#BFFFB0",
  /** Dim neutral text (mockup `--dim`). */
  dim: "#7d8aa0",
  /** Steel accent text (mockup `--steel`). */
  steel: "#cfe0ff",
  /** Dark green accent (mockup `--green-dk`). */
  greenDk: "#1C7A12",
  /** Dark gold lock text (mockup `--gold-dk`). */
  goldDk: "#B8841E",
} as const;

// ─── G. Overlay z-index ladder + dismiss contract (BL-198) ────────────────────

/**
 * The NAMED overlay z-index ladder (BL-198). Before this, every floating overlay
 * hardcoded an ad-hoc z literal (`InfoTooltip` 9999, `SellConfirmDialog` 950,
 * `PauseOverlay` 900, board-chrome 6, `#ui-layer` 4 — UI_AUDIT §B), so the stack
 * order was implicit and un-auditable. This is the single source the overlays
 * route through, named by TIER so the order reads at a glance and a new overlay
 * picks a tier instead of inventing a number.
 *
 * The tiers (low → high), spaced so a sibling effect can slot between two without
 * a renumber:
 *   - `END_SCREEN` — the run-end takeover (Victory / GameOver). Sits above the
 *     board + HUD but below transient modals so a sell/pause can still surface.
 *   - `OVERLAY`    — in-run modal overlays (Pause, the act-card door).
 *   - `DIALOG`     — confirmation dialogs (SellConfirmDialog) — above pause so a
 *     sell prompt raised while paused reads on top.
 *   - `DRAFT`      — the between-wave draft / skill-overview takeover.
 *   - `LEGAL`      — the legal/privacy modal (reachable from menus).
 *   - `TOOLTIP`    — hover tooltips, always the topmost transient hint.
 *
 * Values only; no colour, no new hex. Consumers read `String(UI_Z.<tier>)`.
 */
export const UI_Z = {
  END_SCREEN: 100,
  OVERLAY: 900,
  DIALOG: 950,
  DRAFT: 1000,
  LEGAL: 1200,
  TOOLTIP: 9999,
} as const;

export type ZTier = keyof typeof UI_Z;

/**
 * The documented DISMISS CONTRACT (BL-198 / the BL-119 in-place grammar). Every
 * dismissable overlay should honour the SAME two gestures so dismissal is
 * predictable: a **backdrop click** (clicking the dimmed area outside the panel)
 * and the **Escape key**. A primary confirm may additionally bind Enter. This is
 * a documentation token (the contract in one place); each overlay wires the
 * gestures itself (jsdom-testable) and references this for the rationale.
 */
export const UI_DISMISS = {
  /** Dismiss when the pointer-down target is the backdrop itself (not the panel). */
  backdropClick: true,
  /** Dismiss on the Escape key while visible. */
  escapeKey: true,
} as const;
